import React, { useId, useState } from 'react';
import { Box, Card, CardSection, Flex, Label, Text, VisuallyHidden } from '@indeed/ifl-components';
import type { SystemStyleObject } from '@indeed/ifl-css';
import { CheckCircle } from '@indeed/ifl-icons';

export interface OptionCardProps {
    name: string;
    value: string;
    checked: boolean;
    onChange: () => void;
    icon?: React.ReactNode;
    title: React.ReactNode;
    description?: React.ReactNode;
    sx?: SystemStyleObject;
}

export const OptionCard: React.FC<OptionCardProps> = ({
    name,
    value,
    checked,
    onChange,
    icon,
    title,
    description,
    sx,
}) => {
    const [focused, setFocused] = useState(false);
    const generatedId = useId();
    const inputId = `${name}-${generatedId}`;
    const descriptionId = description ? `${inputId}-desc` : undefined;

    return (
        <Box
            data-checked={checked}
            sx={{
                position: 'relative',
                'label::after': {
                    content: "''",
                    position: 'absolute',
                    insetBlockStart: 0,
                    insetInlineStart: 0,
                    inlineSize: '100%',
                    blockSize: '100%',
                },
                ...sx,
            }}
        >
            <Card
                data-focused={focused}
                sx={{
                    cursor: 'pointer',
                    '[data-checked=true] > &': {
                        borderColor: 'border.primary',
                        boxShadow: ({ colors }) =>
                            `0 0 0 1px ${(colors as Record<string, Record<string, string>>)?.border?.primary ?? 'currentColor'}`,
                    },
                    '&:hover': {
                        borderColor: 'border.primary',
                    },
                    '&[data-focused=true]': {
                        borderWidth: '2px',
                    },
                }}
            >
                <CardSection>
                    <VisuallyHidden>
                        <input
                            type="radio"
                            id={inputId}
                            name={name}
                            value={value}
                            checked={checked}
                            onChange={onChange}
                            onFocus={() => setFocused(true)}
                            onBlur={() => setFocused(false)}
                            aria-describedby={descriptionId}
                        />
                    </VisuallyHidden>
                    <Flex sx={{ alignItems: 'flex-start', gap: 2 }}>
                        {icon && (
                            <Flex
                                as="span"
                                sx={{
                                    flexShrink: 0,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {icon}
                            </Flex>
                        )}
                        <Flex as="span" sx={{ flex: 1, flexDirection: 'column', gap: 1 }}>
                            <Label htmlFor={inputId} sx={{ fontWeight: '700' }}>
                                {title}
                            </Label>
                            {description && (
                                <Text id={descriptionId} level={2}>
                                    {description}
                                </Text>
                            )}
                        </Flex>
                        <OptionCardIndicator />
                    </Flex>
                </CardSection>
            </Card>
        </Box>
    );
};

const indicatorStyles = {
    flexShrink: 0,
    size: '1.5rem',
    '& > svg': {
        opacity: 0,
        transition: ({ transitions }: { transitions?: { default?: string } }) =>
            `opacity ${transitions?.default}`,
        '@media (prefers-reduced-motion)': {
            transitionProperty: 'none',
        },
    },
    '[data-checked=true] & > svg': { opacity: 1 },
};

const OptionCardIndicator: React.FC = () => (
    <Box sx={{ ...indicatorStyles }}>
        <CheckCircle aria-hidden />
    </Box>
);
